import React, { useState } from 'react';
import { PlusIcon, SparklesIcon, DatabaseIcon, DownloadIcon, PlayIcon, MenuIcon } from 'lucide-react';
import { SegmentResultsModal } from './SegmentResultsModal';
import { RunPlayWizard } from './RunPlayWizard';
import { TemplateBrowserModal } from './TemplateBrowserModal';
export function WorkspaceCanvas() {
  const [aiPrompt, setAiPrompt] = useState('');
  const [selectedDataSources, setSelectedDataSources] = useState<string[]>(['zoominfo']);
  const [showSegmentResults, setShowSegmentResults] = useState(false);
  const [showRunPlayWizard, setShowRunPlayWizard] = useState(false);
  const [showTemplateBrowser, setShowTemplateBrowser] = useState(false);
  const [showLeftPanel, setShowLeftPanel] = useState(false);
  const [showRightPanel, setShowRightPanel] = useState(false);
  const dataSources = [{
    id: 'zoominfo',
    name: 'ZoomInfo',
    icon: '🔍',
    records: '250M+',
    color: 'bg-red-50 border-red-200 text-red-700'
  }, {
    id: 'salesforce',
    name: 'Salesforce',
    icon: '☁️',
    records: '45K',
    color: 'bg-blue-50 border-blue-200 text-blue-700'
  }, {
    id: 'product',
    name: 'Product Usage',
    icon: '📊',
    records: '12K',
    color: 'bg-purple-50 border-purple-200 text-purple-700'
  }, {
    id: 'intent',
    name: 'Intent Signals',
    icon: '🎯',
    records: '8.5K',
    color: 'bg-green-50 border-green-200 text-green-700'
  }, {
    id: 'website',
    name: 'Website Activity',
    icon: '🌐',
    records: '23K',
    color: 'bg-orange-50 border-orange-200 text-orange-700'
  }];
  const segments = [{
    id: 1,
    name: 'High-Intent Accounts',
    count: 342,
    sources: ['zoominfo', 'intent', 'website']
  }, {
    id: 2,
    name: 'Enterprise Prospects',
    count: 156,
    sources: ['zoominfo', 'salesforce']
  }];
  const mockSegmentData = {
    name: 'AI-Generated Segment',
    accountCount: 1247,
    confidence: 85,
    topIndustries: [{
      name: 'Software & Technology',
      count: 456
    }, {
      name: 'Financial Services',
      count: 312
    }, {
      name: 'Healthcare',
      count: 289
    }],
    topCompanies: [{
      name: 'Acme Corp',
      size: '500-1000 employees',
      intent: 'High'
    }, {
      name: 'TechStart Inc',
      size: '100-500 employees',
      intent: 'High'
    }, {
      name: 'Global Solutions',
      size: '1000+ employees',
      intent: 'Medium'
    }]
  };
  const handleBuildSegment = () => {
    if (!aiPrompt.trim()) {
      alert('Please enter a description for your target segment');
      return;
    }
    setShowSegmentResults(true);
  };
  const handleRunPlay = () => {
    setShowRunPlayWizard(true);
  };
  const handleImportTemplate = () => {
    setShowTemplateBrowser(true);
  };
  return <div className="w-full h-screen flex flex-col bg-gradient-to-br from-gray-50 via-white to-gray-100" data-id="element-3779">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-200 px-4 lg:px-6 py-3 lg:py-4 shadow-sm" data-id="element-3780">
        <div className="flex items-center justify-between" data-id="element-3781">
          <div className="flex items-center gap-2" data-id="element-3782">
            <button onClick={() => setShowLeftPanel(!showLeftPanel)} className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-all duration-200" data-id="element-3783">
              <MenuIcon className="w-5 h-5" data-id="element-3784" />
            </button>
            <div data-id="element-3785">
              <h1 className="text-lg lg:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent" data-id="element-3786">
                GTM Studio Workspace
              </h1>
              <p className="text-xs lg:text-sm text-gray-600 mt-1 hidden sm:block" data-id="element-3787">
                Build segments and orchestrate GTM plays with AI
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 lg:gap-3" data-id="element-3788">
            <button className="hidden sm:flex items-center gap-2 px-3 lg:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 text-sm shadow-sm hover:shadow" data-id="element-3789">
              <DownloadIcon className="w-4 h-4" data-id="element-3790" />
              <span className="hidden lg:inline" data-id="element-3791">Export</span>
            </button>
            <button onClick={handleRunPlay} className="flex items-center gap-2 px-3 lg:px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 text-sm shadow-md hover:shadow-lg transform hover:-translate-y-0.5" data-id="element-3792">
              <PlayIcon className="w-4 h-4" data-id="element-3793" />
              <span className="hidden sm:inline" data-id="element-3794">Run Play</span>
            </button>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-hidden flex flex-col lg:flex-row" data-id="element-3795">
        {/* Mobile Left Panel Overlay */}
        {showLeftPanel && <>
            <div className="lg:hidden fixed inset-0 bg-black/30 backdrop-blur-sm z-40 animate-in fade-in duration-200" onClick={() => setShowLeftPanel(false)} data-id="element-3796" />
            <div className="lg:hidden fixed left-0 top-0 bottom-0 w-80 bg-white z-50 overflow-y-auto shadow-2xl animate-in slide-in-from-left duration-300" data-id="element-3797">
              <div className="p-4" data-id="element-3798">
                <div className="flex items-center justify-between mb-4" data-id="element-3799">
                  <div className="flex items-center gap-2" data-id="element-3800">
                    <DatabaseIcon className="w-5 h-5 text-red-600" data-id="element-3801" />
                    <h2 className="text-lg font-semibold text-gray-900" data-id="element-3802">
                      Data Sources
                    </h2>
                  </div>
                  <button onClick={() => setShowLeftPanel(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-200" data-id="element-3803">
                    ✕
                  </button>
                </div>
                <div className="space-y-3" data-id="element-3804">
                  {dataSources.map(source => <button key={source.id} onClick={() => {
                setSelectedDataSources(prev => prev.includes(source.id) ? prev.filter(s => s !== source.id) : [...prev, source.id]);
              }} className={`w-full p-4 border-2 rounded-xl text-left transition-all duration-200 transform hover:scale-105 ${selectedDataSources.includes(source.id) ? `${source.color} shadow-lg` : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'}`} data-id="element-3805">
                      <div className="flex items-center gap-3 mb-2" data-id="element-3806">
                        <span className="text-2xl" data-id="element-3807">{source.icon}</span>
                        <div className="flex-1" data-id="element-3808">
                          <p className="font-semibold text-gray-900" data-id="element-3809">
                            {source.name}
                          </p>
                          <p className="text-xs text-gray-600" data-id="element-3810">
                            {source.records} records
                          </p>
                        </div>
                      </div>
                    </button>)}
                </div>
              </div>
            </div>
          </>}
        {/* Desktop Left Panel */}
        <div className="hidden lg:block w-80 bg-white/50 backdrop-blur-sm border-r border-gray-200 overflow-y-auto" data-id="element-3811">
          <div className="p-4" data-id="element-3812">
            <div className="flex items-center gap-2 mb-4" data-id="element-3813">
              <DatabaseIcon className="w-5 h-5 text-red-600" data-id="element-3814" />
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-3815">
                Data Sources
              </h2>
            </div>
            <div className="space-y-3" data-id="element-3816">
              {dataSources.map(source => <button key={source.id} onClick={() => {
              setSelectedDataSources(prev => prev.includes(source.id) ? prev.filter(s => s !== source.id) : [...prev, source.id]);
            }} className={`w-full p-4 border-2 rounded-xl text-left transition-all duration-200 transform hover:scale-105 ${selectedDataSources.includes(source.id) ? `${source.color} shadow-lg` : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'}`} data-id="element-3817">
                  <div className="flex items-center gap-3 mb-2" data-id="element-3818">
                    <span className="text-2xl" data-id="element-3819">{source.icon}</span>
                    <div className="flex-1" data-id="element-3820">
                      <p className="font-semibold text-gray-900" data-id="element-3821">
                        {source.name}
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-3822">
                        {source.records} records
                      </p>
                    </div>
                  </div>
                </button>)}
            </div>
          </div>
        </div>
        {/* Main Canvas */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-3823">
          {/* AI Prompt Input */}
          <div className="bg-white rounded-2xl border-2 border-gray-200 p-4 lg:p-6 mb-4 lg:mb-6 shadow-lg hover:shadow-xl transition-all duration-300" data-id="element-3824">
            <div className="flex items-center gap-2 mb-4" data-id="element-3825">
              <SparklesIcon className="w-5 h-5 text-red-600 animate-pulse" data-id="element-3826" />
              <h3 className="text-base lg:text-lg font-semibold text-gray-900" data-id="element-3827">
                AI Segment Builder
              </h3>
            </div>
            <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} placeholder="Describe your target segment in natural language..." className="w-full p-3 lg:p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none text-sm transition-all duration-200" rows={4} data-id="element-3828" />
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-4" data-id="element-3829">
              <p className="text-xs text-gray-600" data-id="element-3830">
                {selectedDataSources.length} data source
                {selectedDataSources.length !== 1 ? 's' : ''} selected
              </p>
              <button onClick={handleBuildSegment} disabled={!aiPrompt.trim()} className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:-translate-y-0.5" data-id="element-3831">
                <SparklesIcon className="w-4 h-4" data-id="element-3832" />
                Build Segment
              </button>
            </div>
          </div>
          {/* Visual Query Builder */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 lg:p-6 mb-4 lg:mb-6 shadow-md hover:shadow-lg transition-all duration-300" data-id="element-3833">
            <div className="flex items-center justify-between mb-4" data-id="element-3834">
              <h3 className="text-base lg:text-lg font-semibold text-gray-900" data-id="element-3835">
                Visual Query Builder
              </h3>
              <button className="text-sm text-red-600 hover:text-red-700 font-medium transition-colors duration-200" data-id="element-3836">
                + Add Filter
              </button>
            </div>
            <div className="space-y-3" data-id="element-3837">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 p-3 lg:p-4 bg-gradient-to-r from-gray-50 to-transparent rounded-xl border border-gray-200" data-id="element-3838">
                <select className="px-3 py-2 border-2 border-gray-200 rounded-lg text-sm flex-1 focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all duration-200" data-id="element-3839">
                  <option data-id="element-3840">Employee Count</option>
                  <option data-id="element-3841">Revenue</option>
                  <option data-id="element-3842">Industry</option>
                  <option data-id="element-3843">Location</option>
                </select>
                <select className="px-3 py-2 border-2 border-gray-200 rounded-lg text-sm flex-1 focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all duration-200" data-id="element-3844">
                  <option data-id="element-3845">is greater than</option>
                  <option data-id="element-3846">is less than</option>
                  <option data-id="element-3847">equals</option>
                  <option data-id="element-3848">contains</option>
                </select>
                <input type="text" placeholder="Value" className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all duration-200" data-id="element-3849" />
                <button className="sm:px-3 py-2 text-gray-400 hover:text-red-600 self-center transition-colors duration-200" data-id="element-3850">
                  ✕
                </button>
              </div>
            </div>
            <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border-2 border-blue-200 shadow-sm" data-id="element-3851">
              <p className="text-sm text-blue-900 font-medium mb-1" data-id="element-3852">
                Estimated Results
              </p>
              <p className="text-2xl font-bold text-blue-900" data-id="element-3853">1,247 accounts</p>
            </div>
          </div>
          {/* Template Import */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 lg:p-6 shadow-md hover:shadow-lg transition-all duration-300" data-id="element-3854">
            <div className="flex items-center justify-between mb-4" data-id="element-3855">
              <h3 className="text-base lg:text-lg font-semibold text-gray-900" data-id="element-3856">
                Import Play Template
              </h3>
              <button onClick={handleImportTemplate} className="text-sm text-red-600 hover:text-red-700 font-medium transition-colors duration-200" data-id="element-3857">
                Browse
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3858">
              <div onClick={handleImportTemplate} className="p-4 border-2 border-dashed border-gray-300 rounded-xl text-center hover:border-red-500 hover:bg-red-50 transition-all duration-200 cursor-pointer" data-id="element-3859">
                <PlusIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" data-id="element-3860" />
                <p className="text-sm text-gray-600" data-id="element-3861">Import Template</p>
              </div>
              <div onClick={handleImportTemplate} className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200 rounded-xl cursor-pointer hover:border-purple-400 hover:shadow-md transition-all duration-200 transform hover:-translate-y-1" data-id="element-3862">
                <p className="text-sm font-semibold text-purple-900 mb-2" data-id="element-3863">
                  High-Intent Blitz
                </p>
                <p className="text-xs text-purple-700" data-id="element-3864">8 touchpoints</p>
              </div>
              <div onClick={handleImportTemplate} className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-200 rounded-xl cursor-pointer hover:border-green-400 hover:shadow-md transition-all duration-200 transform hover:-translate-y-1" data-id="element-3865">
                <p className="text-sm font-semibold text-green-900 mb-2" data-id="element-3866">
                  Executive Nurture
                </p>
                <p className="text-xs text-green-700" data-id="element-3867">12 touchpoints</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Modals */}
      <SegmentResultsModal isOpen={showSegmentResults} onClose={() => setShowSegmentResults(false)} segmentData={mockSegmentData} data-id="element-3868" />
      <RunPlayWizard isOpen={showRunPlayWizard} onClose={() => setShowRunPlayWizard(false)} data-id="element-3869" />
      <TemplateBrowserModal isOpen={showTemplateBrowser} onClose={() => setShowTemplateBrowser(false)} data-id="element-3870" />
    </div>;
}